import diagnostics from 'scoreboard/components/diagnostics'
import template from './user-permissions-audit.hbs'
import _ from 'lodash'
import $ from 'jquery'
import LoadingDots from 'scoreboard/components/loading-dots'
import conductor from 'scoreboard/components/conductor'
import i18n from 'scoreboard/components/i18n'
import organizationPermissionsHbs from './organization-permissions.hbs'
import advancedDatasetPermissionsHbs from './advanced-dataset-permssions.hbs'
import Dataset from 'scoreboard/datasets/model/dataset'
import DatasetsFilterBadges from 'scoreboard/datasets/filters/datasets-filter-badges'
import DomainType from 'scoreboard/components/domain-type'
const {
	USER,
	GROUP,
	ROLLUP_TREE_GROUP,
	USER_PERMISSIONS_AUDIT
} = DomainType
import constants from 'scoreboard/components/constants'
import {
	renderGroupActions, renderUser, renderGroup, renderDatasetActions,
	renderRollupTreeGroup, onClickRollupTreeGroup, isDisplayingSendTo
} from '../permissions-audit-util'
import PermissionsAudit from '../permissions-audit'
import SinglePrincipalChooser from 'scoreboard/components/single-principal-chooser'
import appDisplay from 'scoreboard/components/app-display'

const UserPermissionsAudit = PermissionsAudit.extend(/** @lends {UserPermissionsAudit} */{

	className: 'admin permissions-audit user-permissions-audit table-hack display-layout',

	events: _.extend({}, PermissionsAudit.prototype.events, {
		'change [name=userId]': 'onChangeUserId',
		'click .template': 'onClickRollupTreeGroup',
		'click .send-to': 'onClickSendTo'
	}),

	/**
	 * The User Permissions Audit (#54662)
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Object} [options.user] a user to show pre-selected in the user
	 * dropdown
	 * @param {Number} [options.user.id] the user's numeric ID
	 * @param {String} [options.user.label] the text to display which represents
	 * the user
	 */
	initialize: function(options = {}) {
		this.options = options
	},

	/**
	 * @override
	 */
	domainObjectInfo () {
		return {
			domainId: this.user.id,
			domainType: USER_PERMISSIONS_AUDIT
		}
	},

	render: function() {
		this.$el.html(template(this.context()))
		this.renderUsersDropdown()
		this.loadingDots = new LoadingDots({ el: this.$('.loading-dots-container') })
		this.fetchAndRenderResponse()
		this.toggle()
		return this
	},

	context: function() {
		let bannerHtml = i18n.get('admin.userPermissionsAudit.banner')
		return { bannerHtml }
	},

	renderUsersDropdown: function() {
		this.singlePrincipalChooser = new SinglePrincipalChooser({
			el: this.$('.user.input-container'),
			selected: this.options?.user,
			url: 'api/admin/users?onlyEditable=true'
		}).render()
	},

	fetchAndRenderResponse: function() {
		let userId = this.userId()
		if (userId && userId != '') {
			this.loadingDots.render()
			this.loadingDots.$el.removeClass('is-not-displayed')
			this.$('.panel-content').addClass('is-not-displayed')
			$.get(`api/admin/user-permissions-audit/${userId}`, response => {
				this.renderResponse(response)
				this.loadingDots.$el.addClass('is-not-displayed')
				this.loadingDots.clear()
				this.$('.panel-content').removeClass('is-not-displayed')
				this.trigger('ready')
			})
		}
	},

	/**
	 * Renders the response returned from the server after selecting a user
	 */
	renderResponse: function(response) {
		this.response = response
		this.user = response.user
		this.groupsById = _.keyBy(response.groups, 'id')
		this.orgsById = _.keyBy(response.organizations, 'id')
		this.treeItemsById = _.keyBy(response.treeItems, 'id')
		this.rollupTreeGroupsById = _.keyBy(response.rollupTreeGroups, 'id')
		this.datasetsById = UserPermissionsAudit.datasetsById(response.datasets)
		this.toggle()
		this.renderOrgPermissions()
		this.renderDatasetPermissions()
		this.updateExportHeader({
			selectionTitle: this.user?.label,
			pageTitle: i18n.get('admin.userPermissionsAudit')
		})
	},

	/**
	 * Renders the Organization Permissions panel
	 */
	renderOrgPermissions: function() {
		if (_.isEmpty(this.response.organizationPermissions)) {
			this.$('.orgs.panel .panel-content').empty()
			return
		}
		let html = ''
		_.each(this.getSortedOrgPermissions(), op => {
			html += '<tr>' +
				'<td>' + this.renderOrgColumn(op) + '</td>' +
				'<td class="permission-source">' +
					'<span class="permission-label-wrapper">' +
						this.renderPermissionSourceColumn(op) +
					'</span>' +
				'</td>' +
				'<td>' + renderGroupActions(op.permissionIds) + '</td>' +
			'</tr>'
		})
		this.$('.orgs.panel .panel-content').html(organizationPermissionsHbs())
			.find('table tbody').html(html)
	},

	/**
	 * Sorts groups in the order of their breadcrumb chain, e.g. A, then A > C,
	 * then B, then B > D, etc.
	 */
	getSortedOrgPermissions: function() {
		return _.sortBy(this.response.organizationPermissions, op => {
			return this.getGroupSortStr(op.groupIds)
		})
	},

	/**
	 * Returns the advanced dataset permissions in the order they should be
	 * displayed in the Advanced Dataset Permissions panel. The order is:
	 * 1. Dataset name
	 * 1. Permissions assigned to the user
	 * 2. Permissions assigned to groups ordered getGroupSortStr
	 * 3. Permissions assigned to templates ordered by field label
	 */
	getSortedDatasetPermissions: function() {
		// do the secondary sort first (items 2-4). then we'll sort by dataset
		// name later which will take precedence
		let sorted = _.sortBy(this.response.advancedDatasetPermissions, dp => {
			if (dp.principalType == USER) {
				// there's only one row for the user, so we can just sort by
				// 1 to indicate the user comes first
				return '1'
			} else if (dp.principalType == GROUP) {
				// for groups, sort by the groupSortStr, and start with 2
				// indicating groups go second
				return '2_' + this.getGroupSortStr(dp.groupIds)
			} else if (dp.principalType == ROLLUP_TREE_GROUP) {
				let dataset = this.datasetsById[dp.datasetId]
				let field = dataset.combinedFieldsById[dp.datasetFieldId]
				// for tree templates, start with 3 to indicate templates go
				// third, then sort by dataset name and field name (#56485)
				return '3_' + field?.label
			}
		})
		// now apply the first sort by dataset name. sorts are stable, so the
		// sorting from above will be maintained within the dataset name sort
		return _.sortBy(sorted, dp => {
			let dataset = this.datasetsById[dp.datasetId]
			return dataset?.get('label')?.toLocaleLowerCase()
		})

	},

	/**
	 * Returns a String that can be used to compare groups for sorting purposes.
	 * The String is just the group names separated by > characters.
	 */
	getGroupSortStr: function(groupIds) {
		return _(groupIds)
			.map(id => this.groupsById[id]?.label?.toLocaleLowerCase())
			.join(' > ')
	},

	/** Renders the Organization column in the Organization Permissions panel */
	renderOrgColumn: function(orgPermissions) {
		// display "All Organizations" when appropriate
		if (orgPermissions?.permissionIds?.includes(constants.SEC_CODE_VIEWALLORGNODES)) {
			return i18n.get('report.common.optionList.organizations.all')
		}

		// display "No Organizations" when appropriate
		if (_.isEmpty(orgPermissions?.organizations)) {
			return i18n.get('admin.userPermissionsAudit.noOrganizations')
		}

		let orgIds = _.keys(orgPermissions?.organizations)
		let sortedOrgIds = _.sortBy(orgIds, orgId => {
			let org = this.orgsById[orgId]
			return org.leftBound
		})
		let html = '<ul>'
		_.each(sortedOrgIds, orgId => {
			let label = this.orgsById[orgId].label
			// if cascade
			if (orgPermissions?.organizations[orgId]) {
				label = i18n.get('admin.groupsByOrganization.viewOrgWithDescendants', label)
			}
			html += '<li>' + _.escape(label) + '</li>'
		})
		html += '</ul>'
		return html
	},

	/**
	 * Renders the Permission Source column in the Organization Permissions
	 * panel
	 */
	renderPermissionSourceColumn: function(permission) {
		if (!_.isEmpty(permission?.groupIds)) {
			return _(permission?.groupIds)
				.map(groupId => renderGroup(this.groupsById[groupId]))
				.join(' &gt; ')
		}

		if (permission?.principalType == ROLLUP_TREE_GROUP) {
			let dataset = this.datasetsById[permission.datasetId]
			let field = dataset?.combinedFieldsById?.[permission.datasetFieldId]
			let template = this.rollupTreeGroupsById[permission.rollupTreeGroupId]
			return renderRollupTreeGroup({
				rollupTreeGroupId: permission.rollupTreeGroupId,
				rollupTreeGroupLabel: template?.label,
				treeRootId: template?.treeRootId,
				fieldLabel: field?.label,
				organizationId: permission.organizationTemplateId
			})
		}

		// if we reach the end here, assume it's the user
		return renderUser(this.user)
	},

	/** Renders the "Advanced Dataset Permissions" panel */
	renderDatasetPermissions: function() {
		if (_.isEmpty(this.response.advancedDatasetPermissions)) {
			this.$('.datasets.panel .panel-content').empty()
			return
		}
		let html = ''
		let sortedPermissions = this.getSortedDatasetPermissions()
		_.each(sortedPermissions, dp => {
			html += '<tr>' +
				'<td>' + this.renderDataset(dp.datasetId) + '</td>' +
				'<td class="permission-source">' +
					'<span class="permission-label-wrapper">' +
						this.renderPermissionSourceColumn(dp) +
					'</span>' +
				'</td>' +
				'<td class="datasets-filter-badges-container"></td>' +
				'<td>' + renderDatasetActions(dp) + '</td>' +
			'</tr>'
		})
		this.$('.datasets.panel .panel-content').html(advancedDatasetPermissionsHbs())
			.find('table tbody').html(html)
		this.renderDatasetsFilterBadges(sortedPermissions)
	},

	/**
	 * Renders the "Permission Filters" column of the "Dataset Permissions" panel
	 */
	renderDatasetsFilterBadges: function(permissions) {
		_.invokeMap(this.datasetsFilterBadges, 'remove')
		this.datasetsFilterBadges = []
		_.each(this.$('.datasets-filter-badges-container'), (el, i) => {
			let permission = permissions[i]
			let dataset = this.datasetsById[permission.datasetId]
			let filterBadges = new DatasetsFilterBadges({
				model: dataset,
				filters: permission.filters,
				isEditable: false,
				treeItemsById: this.treeItemsById,
				noneMessage: '',
				clickable: false,
				datasetsById: this.datasetsById
			})
			this.datasetsFilterBadges[i] = filterBadges
			this.$(el).append(filterBadges.el)
			filterBadges.render()
		})
	},

	/**
	 * Renders the "Dataset" column of the "Advanced Dataset Permissions" panel
	 */
	renderDataset: function(datasetId) {
		let dataset = this.datasetsById[datasetId]
		return `<a class="sp-navigate text-link" href="datasets/${datasetId}">` +
				_.escape(dataset?.get('label')) +
			'</a>'
	},

	/**
	 * Toggles the visibility of form elements as appropriate given the form's
	 * current state
	 */
	toggle: function() {
		this.$('.orgs.panel, .datasets.panel')
			.toggleClass('is-not-displayed', !this.userId())
		this.$('.send-to').toggleClass('is-not-displayed',
			!isDisplayingSendTo(this.userId(), diagnostics.exportEnabled))
	},

	/**
	 * Returns the current user ID
	 */
	userId: function() {
		let userId = this.$('[name=userId]').val()
		if (userId === 'undefined' || userId === '') {
			userId = undefined
		}
		return userId ??
			conductor.id() ??
			this.options?.user?.id
	},

	onChangeUserId: function() {
		let userIdFromSelect = this.$('[name=userId]').val()
		// change the URL
		conductor.navigate('admin/user-permissions-audit/' + userIdFromSelect)
		// show the panels so we can see loading dots
		this.$('.panel').removeClass('is-not-displayed')
		this.fetchAndRenderResponse()
	},

	onClickRollupTreeGroup

}, {

	dialog: function(userId) {
		if (userId) {
			let xhr = $.get({ url: `/api/admin/users/${userId}` })
			appDisplay.display(xhr, user =>
				new UserPermissionsAudit({ user }))
		} else {
			appDisplay.display(new UserPermissionsAudit())
		}
	},

	datasetsById: function(datasets) {
		// #56484 make sure each dataset has access to all the fields
		let combinedFields = []
		_.each(datasets, dataset => {
			combinedFields = combinedFields.concat(dataset.fields)
			_.each(dataset.linkedDatasets, linkedDataset => {
				combinedFields = combinedFields.concat(linkedDataset.fields)
			})
		})
		let combinedFieldsById = _.keyBy(combinedFields, 'id')
		let allDatasets = _(datasets)
			// #56484 be sure to look at the linked datasets too!
			.flatMap(dataset => dataset.linkedDatasets)
			.filter(dataset => !_.isNil(dataset))
			.concat(datasets)
			.value()
		let datasetsById = _(allDatasets)
			.map(dataset => new Dataset(dataset, { combinedFieldsById}))
			.keyBy('id')
			.value()
		// for #56484 we want all the datasets to know about all the other
		// datasets
		_.each(_.values(datasetsById), dataset => {
			dataset.datasetsById = datasetsById
		})
		return datasetsById
	}

})

export default UserPermissionsAudit

UserPermissionsAudit.__filename__ = 'admin/user-permissions-audit/user-permissions-audit.js'
